import { create } from 'zustand';
import { hasPermission, type Permission, type Role } from '@bruhat/shared';
import { api, clearSession, setActiveCompany, setTokens, getSession } from '@/lib/api-client';

export interface CompanyAccess {
  id: string;
  name: string;
  code: string;
  role: Role;
  roleLabel: string;
  isDefault: boolean;
}

export interface Profile {
  id: string;
  email: string;
  fullName: string;
  avatarUrl: string | null;
  isSuperAdmin: boolean;
  mustChangePassword: boolean;
  activeCompanyId: string | null;
  roles: Role[];
  /** Names of the company's own roles the user holds; `roles` lists built-in ones. */
  customRoles?: string[];
  permissions: Permission[];
  projectIds: string[];
  companies: CompanyAccess[];
}

interface FinancialYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isLocked: boolean;
}

interface AuthState {
  profile: Profile | null;
  companyId: string | null;
  financialYear: FinancialYear | null;
  financialYears: FinancialYear[];
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  setFinancialYear: (id: string) => void;
  can: (permission: Permission) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  profile: null,
  companyId: getSession().companyId,
  financialYear: null,
  financialYears: [],
  status: 'idle',
  error: null,

  async login(email, password) {
    set({ status: 'loading', error: null });
    try {
      const result = await api.post<{
        accessToken: string;
        refreshToken: string;
        defaultCompanyId: string | null;
      }>('/auth/login', { email, password }, { withoutCompany: true });

      setTokens(result.accessToken, result.refreshToken);
      if (result.defaultCompanyId) setActiveCompany(result.defaultCompanyId);

      await get().bootstrap();
    } catch (error) {
      set({ status: 'unauthenticated', error: error instanceof Error ? error.message : 'Sign-in failed' });
      throw error;
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout', { refreshToken: getSession().refreshToken });
    } catch {
      // A failed logout call must not trap the user in the app.
    }
    clearSession();
    set({ profile: null, companyId: null, financialYear: null, financialYears: [], status: 'unauthenticated' });
  },

  async bootstrap() {
    if (!getSession().accessToken) {
      set({ status: 'unauthenticated' });
      return;
    }

    set({ status: 'loading' });
    try {
      const profile = await api.get<Profile>('/auth/me');

      // Fall back to the first accessible company when none is selected yet.
      let companyId = getSession().companyId ?? profile.activeCompanyId;
      if (!companyId || !profile.companies.some((c) => c.id === companyId)) {
        companyId = profile.companies.find((c) => c.isDefault)?.id ?? profile.companies[0]?.id ?? null;
      }
      if (companyId) setActiveCompany(companyId);

      // Only /auth/me decides whether the session is valid. Everything below is
      // context the shell would like to have — a role that cannot read it still
      // gets signed in, rather than being bounced back to the login screen.
      let financialYears: FinancialYear[] = [];
      if (companyId) {
        try {
          financialYears = await api.get<FinancialYear[]>('/financial-years');
        } catch {
          financialYears = [];
        }
      }

      set({
        profile,
        companyId,
        financialYears,
        financialYear: financialYears.find((f) => f.isActive) ?? financialYears[0] ?? null,
        status: 'authenticated',
        error: null,
      });
    } catch {
      clearSession();
      set({ status: 'unauthenticated', profile: null });
    }
  },

  async switchCompany(companyId) {
    setActiveCompany(companyId);
    set({ companyId, financialYear: null, financialYears: [] });
    await get().bootstrap();
  },

  setFinancialYear(id) {
    const financialYear = get().financialYears.find((f) => f.id === id) ?? null;
    set({ financialYear });
  },

  can(permission) {
    const profile = get().profile;
    if (!profile) return false;
    if (profile.isSuperAdmin) return true;
    return hasPermission(profile.permissions, permission);
  },
}));
